import type React from "react";
import { useEffect, useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Chip,
  Typography,
} from "@mui/material";
import { useSelector } from "react-redux";
import { useIntroductionLetterTemplates } from "./hooks/use-introduction-letter";
import type {
  IntroductionLetterTemplate,
  TemplateFormData,
} from "./types/introduction-letter";
import { RootState } from "../../../store/Index";
import CustomTextField from "../../../components/common/Textfield";

interface CreateIntroductionLetterModalProps {
  open: boolean;
  onClose: () => void;
  editTemplate?: IntroductionLetterTemplate | null;
  onSave: () => void;
}

const EXAMPLE_VALUES: Record<string, string> = {
  client_name: "Mr. John Smith",
  event_title: "Memorial Service",
  deceased_name: "Jane Doe",
};

const VALID_PLACEHOLDERS = Object.keys(EXAMPLE_VALUES);

const CreateIntroductionLetterModal: React.FC<
  CreateIntroductionLetterModalProps
> = ({ open, onClose, editTemplate, onSave }) => {
  const theme = useSelector((state: RootState) => state.theme);
  const { saveTemplate, updateTemplate, loading } =
    useIntroductionLetterTemplates();

  const [templateData, setTemplateData] = useState<TemplateFormData>({
    name: "",
    category: "Funeral",
    body_template: "",
  });

  useEffect(() => {
    if (editTemplate) {
      setTemplateData({
        name: editTemplate.name,
        category: editTemplate.category,
        body_template: editTemplate.body_template,
      });
    } else {
      setTemplateData({
        name: "",
        category: "Funeral",
        body_template: "",
      });
    }
  }, [editTemplate, open]);

  const handleChange = (field: keyof TemplateFormData, value: string) => {
    setTemplateData((prev) => ({ ...prev, [field]: value }));
  };

  const insertPlaceholder = (placeholder: string) => {
    const textarea = document.getElementById(
      "highlighted-textarea"
    ) as HTMLTextAreaElement | null;

    if (!textarea) return;

    const start = textarea.selectionStart;
    const end = textarea.selectionEnd;

    const newValue =
      templateData.body_template.slice(0, start) +
      placeholder +
      templateData.body_template.slice(end);

    setTemplateData((prev) => ({ ...prev, body_template: newValue }));

    setTimeout(() => {
      textarea.selectionStart = textarea.selectionEnd =
        start + placeholder.length;
      textarea.focus();
    }, 0);
  };

  const highlightVariables = (text: string) => {
    let result = text;

    VALID_PLACEHOLDERS.forEach((key) => {
      const placeholder = `{${key}}`;

      result = result.replaceAll(
        placeholder,
        `<span style="color: red; font-weight: bold">${EXAMPLE_VALUES[key]}</span>`
      );
    });

    return result;
  };

  const handleSave = async () => {
    if (editTemplate?.id) {
      await updateTemplate(editTemplate.id, templateData);
    } else {
      await saveTemplate(templateData);
    }
    onSave();
    onClose();
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle sx={{ color: theme.primary_colors[100] }}>
        {editTemplate
          ? "Edit Introduction Letter Template"
          : "Create Introduction Letter Template"}
      </DialogTitle>

      <DialogContent sx={{ backgroundColor: theme.primary_colors[900] }}>
        <Box display="flex" gap={3} mt={2}>
          <Box flex={2} display="flex" flexDirection="column" gap={3}>
            <CustomTextField
              label="Template Name"
              fullWidth
              value={templateData.name}
              onChange={(e) => handleChange("name", e.target.value)}
              variant="outlined"
            />

            <CustomTextField
              label="Category"
              fullWidth
              value={templateData.category}
              onChange={(e) => handleChange("category", e.target.value)}
              variant="outlined"
            />

            <CustomTextField
              id="highlighted-textarea"
              label="Introduction Letter Body"
              multiline
              minRows={10}
              value={templateData.body_template}
              onChange={(e) => handleChange("body_template", e.target.value)}
              variant="outlined"
            />

            <Typography
              variant="subtitle2"
              sx={{ color: theme.primary_colors[300] }}
            >
              Available variables for your introduction letter:
            </Typography>

            <Box display="flex" flexWrap="wrap" gap={1}>
              {VALID_PLACEHOLDERS.map((p) => (
                <Chip
                  key={p}
                  label={`{${p}}`}
                  onClick={() => insertPlaceholder(`{${p}}`)}
                  sx={{
                    backgroundColor: theme.primary_colors[900],
                    color: theme.primary_colors[400],
                    border: "1px solid #614338",
                    fontWeight: 600,
                    "&:hover": {
                      backgroundColor: theme.primary_colors[500],
                    },
                  }}
                />
              ))}
            </Box>
          </Box>

          <Box
            flex={1}
            p={2}
            border={`1px solid ${theme.primary_colors[400]}`}
            borderRadius={2}
            bgcolor={theme.primary_colors[900]}
            height="fit-content"
            maxHeight="500px"
            overflow="auto"
            boxShadow="inset 0 0 4px rgba(0,0,0,0.2)"
          >
            <Typography
              fontWeight="bold"
              mb={2}
              sx={{ color: theme.primary_colors[100] }}
            >
              Live Preview
            </Typography>

            <Typography
              component="div"
              whiteSpace="pre-wrap"
              sx={{ lineHeight: 1.6 }}
              dangerouslySetInnerHTML={{
                __html:
                  highlightVariables(templateData.body_template) ||
                  "<em>Start typing to see preview...</em>",
              }}
            />
          </Box>
        </Box>
      </DialogContent>

      <DialogActions sx={{ p: 2, backgroundColor: theme.primary_colors[900] }}>
        <Button onClick={onClose} sx={{ color: theme.primary_colors[300] }}>
          Cancel
        </Button>
        <Button
          disabled={
            loading || !templateData.name || !templateData.body_template
          }
          onClick={handleSave}
          variant="contained"
          sx={{
            backgroundColor: theme.primary_colors[400],
            color: theme.primary_colors[900],
            "&:hover": {
              backgroundColor: theme.primary_colors[500],
            },
          }}
        >
          {editTemplate ? "Update" : "Save"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default CreateIntroductionLetterModal;
